import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Box, Menu, MenuItem, useTheme } from '@mui/material';

import CustomButton from '../../styles/CustomButtons';

import type { RouteItem } from './Nav.types';

interface NavDropdownItemProps {
  route: RouteItem;
  onNavigate: (path: string, pageName: string) => void;
}

export const NavDropdownItem: React.FC<NavDropdownItemProps> = ({ route, onNavigate }) => {
  const location = useLocation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isOpen = Boolean(anchorEl);

  // Clear any pending close timeout
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  // Schedule closing with delay
  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  }, [clearCloseTimeout]);

  // Handle mouse entering the button
  const handleButtonMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      clearCloseTimeout();
      setAnchorEl(event.currentTarget);
    },
    [clearCloseTimeout],
  );

  // Handle mouse leaving the button
  const handleButtonMouseLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  // Handle mouse entering the menu
  const handleMenuMouseEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  // Handle mouse leaving the menu
  const handleMenuMouseLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  // Click handler (for accessibility/mobile)
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      clearCloseTimeout();
      setAnchorEl(prev => (prev ? null : event.currentTarget));
    },
    [clearCloseTimeout],
  );

  const handleClose = useCallback(() => {
    clearCloseTimeout();
    setAnchorEl(null);
  }, [clearCloseTimeout]);

  // Calculate active state based on children paths
  const isChildActive = route.children?.some(
    child =>
      child.path !== null &&
      (location.pathname === child.path || location.pathname.startsWith(child.path)),
  );

  // Button style based on active state
  const buttonStyle = {
    fontWeight: isChildActive ? 'bold' : 'normal',
    color: isChildActive ? theme.palette.primary.light : 'textColors.lightHeader',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, [clearCloseTimeout]);

  return (
    <>
      <CustomButton
        sx={buttonStyle}
        variant="text"
        onClick={handleClick}
        onMouseEnter={handleButtonMouseEnter}
        onMouseLeave={handleButtonMouseLeave}
        aria-controls={isOpen ? `${route.id}-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
      >
        {route.pageName}
        {route.badgeCount && route.badgeCount > 0 ? (
          <Box
            component="span"
            sx={{
              ml: 0.75,
              px: 0.75,
              height: 18,
              minWidth: 18,
              borderRadius: '9px',
              bgcolor: 'error.main',
              color: 'white',
              fontSize: '0.65rem',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {route.badgeCount > 99 ? '99+' : route.badgeCount}
          </Box>
        ) : null}
        <ExpandMoreIcon
          sx={{
            fontSize: '1rem',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </CustomButton>
      <Menu
        id={`${route.id}-menu`}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': {
            pointerEvents: 'auto',
            minWidth: 180,
            borderRadius: 2,
            bgcolor: 'grey.100',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          },
        }} slotProps={{
          list: {
            'aria-labelledby': `${route.id}-button`,
          },
          paper: {
            onMouseEnter: handleMenuMouseEnter,
            onMouseLeave: handleMenuMouseLeave,
          },
        }}
      >
        {route.children
          ?.filter(child => !child.condition || child.condition())
          .map(child => {
            const isExternalLink = Boolean(child.externalUrl);
            const isPlaceholder = child.path === null && !isExternalLink;
            const childPath = child.path;
            const isChildActive =
              !isPlaceholder &&
              !isExternalLink &&
              childPath !== null &&
              (location.pathname === childPath || location.pathname.startsWith(childPath));

            // Build component props based on link type
            const menuItemProps: Record<string, unknown> = {};
            if (isExternalLink) {
              menuItemProps.component = 'a';
              menuItemProps.href = child.externalUrl;
              if (child.externalLinkProps) {
                Object.assign(menuItemProps, child.externalLinkProps);
              }
            } else if (!isPlaceholder) {
              menuItemProps.component = Link;
              menuItemProps.to = childPath ?? undefined;
            } else {
              menuItemProps.component = 'div';
            }

            return (
              <MenuItem
                key={child.id}
                data-testid={child.testId}
                disabled={isPlaceholder}
                {...menuItemProps}
                onClick={() => {
                  if (!isPlaceholder && child.path) {
                    onNavigate(child.path, child.pageName);
                  }
                  if (!isPlaceholder) {
                    handleClose();
                  }
                }}
                sx={{
                  fontSize: '0.9rem',
                  py: 1,
                  fontWeight: isChildActive ? 'bold' : 'normal',
                  color: isPlaceholder
                    ? theme.palette.text.disabled
                    : isChildActive
                      ? theme.palette.primary.light
                      : 'textColors.lightHeader',
                  bgcolor: isChildActive ? 'action.selected' : 'transparent',
                  cursor: isPlaceholder ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: isPlaceholder ? 'transparent' : theme.palette.action.hover,
                  },
                }}
              >
                {child.pageName}
                {!isPlaceholder && child.badgeCount && child.badgeCount > 0 ? (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      px: 0.75,
                      height: 18,
                      minWidth: 18,
                      borderRadius: '9px',
                      bgcolor: 'error.main',
                      color: 'white',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {child.badgeCount > 99 ? '99+' : child.badgeCount}
                  </Box>
                ) : null}
                {isPlaceholder && (
                  <span style={{ marginLeft: '8px', fontSize: '0.75rem', opacity: 0.7 }}>
                    (Coming soon)
                  </span>
                )}
              </MenuItem>
            );
          })}
      </Menu>
    </>
  );
};
